import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Car } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { amountSchema, type Approval, type EstimateDetail, type UpdateEstimateInput } from 'used-vehicle-contracts';
import { orpcClient } from '../../apis/orpc-client';
import { orpc } from '../../apis/orpc-query';
import { useAuth } from '../../auth/AuthContext';
import { Badge, type BadgeTone } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { DefinitionList } from '../../components/ui/DefinitionList';
import { Dialog } from '../../components/ui/Dialog';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { StatusText } from '../../components/ui/StatusText';
import { Textarea } from '../../components/ui/Textarea';
import { formatMoney } from '../../lib/format';
import { fitsSchema, refusalReasons, serverMessage, submitErrorKey } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { CommercialTargets } from './CommercialTargets';
import { estimateStatusTone, toOptionalNumber } from './estimates-display';
import type { LocalValuation } from './valuation-local';

type FactsPatch = Omit<UpdateEstimateInput, 'estimateId' | 'expectedRevision'>;

export interface EstimatorStep3Props {
  detail: EstimateDetail;
  local: { valuation: LocalValuation } | null;
  /** The tenant's calendar day, so the purchase date cannot be tomorrow where the dealership is. */
  today: string;
  onChange: (patch: Partial<Record<string, string>>, save: FactsPatch | null) => void;
  /** The record moved: the page refetches and adopts the server's copy. */
  onRecordChanged: () => Promise<void>;
  /**
   * Writes whatever the wizard still owes the service and answers with the revision the record is on
   * afterwards. An offer recorded on top of a debounced autosave would be answered with a 409, and the
   * approval it is meant to support would bind to a vehicle that is missing the last thing typed.
   */
  onSavePending: () => Promise<number>;
  /** The asking price as the form currently holds it, so a keystroke is not lost on re-render. */
  askingPrice: string;
}

const APPROVAL_TONES: Record<string, BadgeTone> = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger' };

/**
 * Step 3 — the result, the negotiation and the outcome (plan §7).
 *
 * This is the page where the platform stops calculating and starts committing. Three things follow from
 * that and shape everything below:
 *
 *  - **One figure leads: the calculated price** — the most CTA may pay (business review 2026-09-23,
 *    plan §11.19). What the car is worth, what CTA expects to spend after buying it and what it must
 *    sell for are rows of "Why this price?" beneath it, in plain words, so the one number a salesperson
 *    quotes is never confused with the other two.
 *  - **The offer is typed here, not saved as you type.** `shareOffer` takes the figure directly, so a
 *    number that is being considered never touches the record: only the one that was actually told to the
 *    customer does, with a snapshot to prove what it was based on (plan §5.6).
 *  - **The purchase button is honest.** Its blockers are the server's own rule (`purchaseBlockers`),
 *    listed under it in plain words, rather than a disabled button nobody can explain.
 */
export const EstimatorStep3 = ({ detail, local, today, onChange, onRecordChanged, onSavePending, askingPrice }: EstimatorStep3Props) => {
  const { t } = useI18nTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const outputs = local?.valuation.run.outputs ?? detail.valuation.outputs;
  const missing = local?.valuation.built.missing ?? detail.valuation.missing;
  const blocking = local?.valuation.built.blocking ?? detail.valuation.blocking;
  const trace = local?.valuation.run.trace ?? {};

  const ceiling = outputs.ceiling ?? null;
  const isClosed = detail.status === 'DEAL_AGREED' || detail.status === 'NO_DEAL';

  const [offer, setOffer] = useState<string>(() => (detail.ctaOffer === null ? (ceiling === null ? '' : String(ceiling)) : String(detail.ctaOffer)));
  const [offerTouched, setOfferTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The rule keys the refusal named, listed under the sentence in the same words the button uses. */
  const [errorReasons, setErrorReasons] = useState<string[]>([]);
  const [noDealOpen, setNoDealOpen] = useState(false);
  const [noDealReason, setNoDealReason] = useState('');
  const [askingPriceOutOfRange, setAskingPriceOutOfRange] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchase, setPurchase] = useState({ price: '', date: today, status: 'IN_WAREHOUSE', location: 'Amman used-vehicle warehouse' });

  const shareOffer = useMutation(orpc.estimates.shareOffer.mutationOptions());
  const closeNoDeal = useMutation(orpc.estimates.closeNoDeal.mutationOptions());
  const reopen = useMutation(orpc.estimates.reopen.mutationOptions());
  const confirmPurchase = useMutation(orpc.estimates.purchase.mutationOptions());
  const requestApproval = useMutation(orpc.approvals.request.mutationOptions());
  const decideApproval = useMutation(orpc.approvals.decide.mutationOptions());

  const isBusy =
    shareOffer.isPending ||
    closeNoDeal.isPending ||
    reopen.isPending ||
    confirmPurchase.isPending ||
    requestApproval.isPending ||
    decideApproval.isPending;

  const offerValue = offerTouched ? toOptionalNumber(offer) : (detail.ctaOffer ?? ceiling);
  const aboveCeiling = offerValue !== null && ceiling !== null && offerValue > ceiling;
  const askingValue = toOptionalNumber(askingPrice) ?? detail.customerAskingPrice;
  const difference = offerValue !== null && askingValue !== null ? offerValue - askingValue : null;

  const approval = detail.approval;
  /**
   * Why the approval cannot be asked for yet, as a key the page prints under the button. Both are the
   * service's own refusals (`approvals.estimateNotReady`, `approvals.vinRequired`): a button that fires
   * a request the server is certain to refuse is worse than one that says what is missing.
   */
  const approvalBlocker =
    detail.status !== 'READY_TO_OFFER' && detail.status !== 'OFFER_SHARED'
      ? 'estimates.blockers.notReady'
      : (detail.vinNormalized ?? detail.vin ?? '').trim() === ''
        ? 'approvals.vinRequired'
        : null;

  /**
   * An approval that already covers the figure on screen. Pressing again would only create a second
   * identical one, so the badge beside the button is the answer. A **consumed** approval cannot appear
   * here: spending one purchases the vehicle, which closes the estimate and hides all of this.
   */
  const isApprovalSettled =
    approval !== null &&
    approval.status === 'APPROVED' &&
    !approval.isStale &&
    approval.purchaseMax !== null &&
    offerValue !== null &&
    approval.purchaseMax >= offerValue;

  /** While a request is waiting there is nothing to ask for: the service allows one open request. */
  const isApprovalWaiting = approval !== null && approval.status === 'PENDING';

  /**
   * The purchase dialog's price, against the two limits the rule will check it against (plan §5.6).
   *
   * `detail.purchaseBlockers` answers "could this be bought at the offer it carries?" — it is computed
   * with no payload, so it says nothing about a different figure typed into the dialog. A price above
   * the ceiling, or above what the approval allows, used to pass the enabled button and come back as a
   * 400; the two sentences are now printed under the button that stays disabled, in the same words the
   * server would have used.
   */
  const purchasePrice = toOptionalNumber(purchase.price);
  const approvedMax = approval !== null && approval.status === 'APPROVED' && !approval.isStale ? approval.purchaseMax : null;

  const purchasePriceBlockers =
    purchasePrice === null || ceiling === null || purchasePrice <= ceiling
      ? []
      : approvedMax === null
        ? ['aboveCeiling']
        : purchasePrice > approvedMax
          ? ['aboveApprovedMax']
          : [];

  const canConfirmPurchase = purchasePrice !== null && purchase.location.trim() !== '' && purchasePriceBlockers.length === 0;

  const run = async (action: () => Promise<unknown>): Promise<boolean> => {
    setError(null);
    setErrorReasons([]);

    try {
      await action();
      await queryClient.invalidateQueries({ queryKey: orpc.estimates.list.key() });
      await queryClient.invalidateQueries({ queryKey: orpc.vehicles.list.key() });
      await onRecordChanged();

      return true;
    } catch (caught: unknown) {
      setError(serverMessage(caught) ?? t(submitErrorKey(caught)));
      // A rule refusal names every blocker at once; printed here they read as the same sentences the
      // disabled purchase button lists, instead of one generic "that is not possible" (plan §5.6).
      setErrorReasons(refusalReasons(caught));

      return false;
    }
  };

  /**
   * The approval binds to the **recorded** offer, never to the figure being typed — asking before the
   * number is written is exactly what the service refuses with `approvals.offerRequired`. So the wizard
   * writes what it still owes, and the offer on screen is recorded, before anybody asks for anything.
   */
  const recordTypedOffer = async (): Promise<void> => {
    const revision = await onSavePending();

    if (offerValue === null || detail.ctaOffer === offerValue) {
      return;
    }

    await shareOffer.mutateAsync({ estimateId: detail.id, expectedRevision: revision, ctaOffer: offerValue });
  };

  /**
   * The request that is waiting for a decision, read back in full: the summary on the estimate detail
   * carries no revision, and `decide` needs the one the row is actually on.
   */
  const waitingApproval = async (approvalId: string): Promise<Approval | null> => {
    const { items } = await orpcClient.approvals.listByOwner({ owner: { type: 'ESTIMATE', id: detail.id } });

    return items.find((item) => item.id === approvalId) ?? null;
  };

  /**
   * One press for an administrator: record the figure on screen, ask for the exception, then grant it.
   *
   * A request that is **already** waiting is decided as it stands, and the typed figure is not recorded
   * on top of it. The offer is part of the fingerprint the request is bound to, so re-recording it
   * would make the approval stale the moment it was granted — approved, and still refused at the
   * purchase (plan §5.6). Asking for a second request instead is not open either: the service allows
   * one open request per estimate. Pressing again once this one is decided records the new figure.
   *
   * A waiting request that is **already stale** — the estimate moved after it was asked for — is the
   * one case where granting it as it stands is pointless: the approval would be born stale. It is
   * rejected as superseded instead, which is what frees the estimate for a fresh request bound to the
   * figure on screen, and the press carries on as if nothing had been waiting.
   */
  const approveInline = async () => {
    await run(async () => {
      let waiting = approval !== null && approval.status === 'PENDING' ? await waitingApproval(approval.id) : null;

      if (waiting !== null && waiting.isStale) {
        await decideApproval.mutateAsync({
          approvalId: waiting.id,
          expectedRevision: waiting.revision,
          decision: 'REJECTED',
          reason: t('estimates.approvalSupersededReason'),
        });
        waiting = null;
      }

      if (waiting === null) {
        await recordTypedOffer();
      }

      const pending = waiting ?? (await requestApproval.mutateAsync({ estimateId: detail.id, reason: t('estimates.approvalInlineReason') }));
      // A limit may only be **lowered** from what was asked for (plan §5.6), and a request that was
      // already waiting may have been made at a different figure: never ask for more than it carries.
      const requested = pending.request.ctaOffer;
      const purchaseMax = offerValue === null ? null : requested === null ? offerValue : Math.min(offerValue, requested);

      await decideApproval.mutateAsync({
        approvalId: pending.id,
        expectedRevision: pending.revision,
        decision: 'APPROVED',
        ...(purchaseMax === null ? {} : { purchaseMax }),
      });
    });
  };

  /** The salesperson's half of the same sequence: record the offer, then ask for it to be approved. */
  const requestInline = async () => {
    await run(async () => {
      await recordTypedOffer();
      await requestApproval.mutateAsync({ estimateId: detail.id, reason: t('estimates.approvalInlineReason') });
    });
  };

  const referencePrice = local?.valuation.bundle.facts.referencePrice ?? detail.referencePrice;
  const riskBuffer = local?.valuation.built.inputs.riskBuffer ?? null;

  /** The cost categories the run priced, with what the equation worked out and what the items resolved to. */
  const costRows = Object.values(trace)
    .filter((entry) => entry.cost !== undefined)
    .map((entry) => ({ id: entry.id, label: t(`estimates.costLines.${entry.id}`, { defaultValue: entry.label }), cost: entry.cost! }))
    .filter((entry) => entry.cost.estimate !== 0 || entry.cost.resolved !== 0 || entry.cost.source === 'MANUAL');

  const breakdownRows = [
    { key: 'reference', label: t('estimates.whyReference'), value: referencePrice },
    { key: 'base', label: t('estimates.whyBase'), value: outputs.base ?? null },
    { key: 'inspected', label: t('estimates.whyInspected'), value: outputs.inspected ?? null },
    { key: 'inclusion', label: t('estimates.whyInclusions'), value: outputs.inclusion ?? null },
    { key: 'appraisal', label: t('estimates.whyAppraisal'), value: outputs.appraisal ?? null },
  ];

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
      <div className="space-y-4">
        {isClosed ? (
          <section className="rounded-lg border border-border bg-muted px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <StatusText
                  label={t(`estimates.statuses.${detail.status}`)}
                  tone={estimateStatusTone(detail.status)}
                />
                <p className="mt-1 text-sm text-muted-foreground">
                  {detail.status === 'DEAL_AGREED' ? t('estimates.closedPurchasedBody') : t('estimates.closedNoDealBody')}
                </p>
                {detail.outcomeNote ? <p className="mt-1 text-sm text-foreground">{detail.outcomeNote}</p> : null}
              </div>
              {detail.status === 'NO_DEAL' && user?.role === 'ADMIN' ? (
                <Button
                  variant="outline"
                  size="sm"
                  isLoading={reopen.isPending}
                  onClick={() => void run(async () => reopen.mutateAsync({ estimateId: detail.id, expectedRevision: detail.revision }))}
                >
                  {t('estimates.reopen')}
                </Button>
              ) : null}
            </div>
          </section>
        ) : null}

        <section className="space-y-4 rounded-lg border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-card-foreground">{t('estimates.resultTitle')}</h2>

          {missing.length > 0 ? (
            <p
              role="alert"
              className="rounded-md border border-[#a16207]/30 bg-[#a16207]/5 px-3 py-2 text-sm text-[#a16207]"
            >
              {t('estimates.resultMissing', {
                fields: missing.map((entry) => t(`estimates.fields.${entry.field}`, { defaultValue: entry.field })).join(t('common.listSeparator')),
              })}
            </p>
          ) : null}

          {blocking.length > 0 ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              <p className="font-medium">{t('estimates.blockingTitle')}</p>
              <ul className="mt-1 space-y-1">
                {blocking.map((entry) => (
                  <li key={`${entry.code}-${entry.field}`}>{entry.detail}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('estimates.calculatedPrice')}</p>
            <p
              className="mt-1 text-3xl font-semibold text-foreground"
              data-testid="calculated-price"
            >
              <Money value={ceiling} />
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{t('estimates.calculatedPriceHint')}</p>
            {detail.template ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('estimates.ceilingCaption', { template: detail.template.name, version: detail.template.version })}
              </p>
            ) : null}
          </div>

          <details open>
            <summary className="cursor-pointer text-sm font-medium text-card-foreground">{t('estimates.whyThisPrice')}</summary>
            <DefinitionList
              className="mt-2"
              items={breakdownRows.map((row) => ({
                label: row.label,
                value: (
                  <Money
                    value={row.value}
                    exact
                  />
                ),
              }))}
            />

            <CommercialTargets
              detail={detail}
              local={local}
              profitAllowance={outputs.profitAllowance ?? null}
              riskBuffer={riskBuffer}
              disabled={isClosed}
              onSave={(save) => onChange({}, save)}
            />

            <DefinitionList
              className="mt-2"
              items={[
                {
                  label: <span className="font-semibold text-foreground">{t('estimates.whyCeiling')}</span>,
                  value: (
                    <span className="font-semibold">
                      <Money
                        value={ceiling}
                        exact
                      />
                    </span>
                  ),
                },
                {
                  label: (
                    <span>
                      {t('estimates.whyCosts')}
                      <span className="block text-xs text-muted-foreground">{t('estimates.whyCostsNote')}</span>
                    </span>
                  ),
                  value: (
                    <Money
                      value={outputs.costs ?? null}
                      exact
                    />
                  ),
                },
                {
                  label: t('estimates.whyRetail'),
                  value: (
                    <Money
                      value={outputs.retail ?? null}
                      exact
                    />
                  ),
                },
              ]}
            />

            {costRows.length > 0 ? (
              <div className="mt-3 rounded-md border border-border bg-muted/40 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('estimates.whyCostRows')}</p>
                <DefinitionList
                  items={costRows.map((row) => ({
                    label: (
                      <span>
                        {row.label}
                        {row.cost.source === 'MANUAL' ? (
                          <span className="block text-xs text-muted-foreground">
                            {row.id === 'transfer' ? t('estimates.costSourceManualIncluded') : t('estimates.costSourceManual')}
                          </span>
                        ) : row.id === 'registration' || row.id === 'transfer' ? (
                          <span className="block text-xs text-muted-foreground">
                            {row.cost.source === 'ITEMS' ? t('estimates.costSourceItems') : t('estimates.costSourceTemplate')}
                          </span>
                        ) : null}
                      </span>
                    ),
                    value:
                      row.cost.estimate === row.cost.resolved ? (
                        <Money
                          value={row.cost.resolved}
                          exact
                        />
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          <span className="text-muted-foreground line-through">{formatMoney(row.cost.estimate, { exact: true })}</span>
                          <span aria-hidden>→</span>
                          <Money
                            value={row.cost.resolved}
                            exact
                          />
                        </span>
                      ),
                  }))}
                />
              </div>
            ) : null}
          </details>
        </section>

        <section className="space-y-4 rounded-lg border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-card-foreground">{t('estimates.negotiationTitle')}</h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              id="estimate-asking-price"
              label={t('estimates.customerAskingPrice')}
              error={askingPriceOutOfRange ? t('estimates.numberOutOfRange') : undefined}
            >
              <Input
                id="estimate-asking-price"
                type="number"
                min={0}
                step={0.001}
                disabled={isClosed}
                value={askingPrice}
                onChange={(event) => {
                  // Same rule as step 1's typed numbers: an amount the contract would refuse is shown as
                  // wrong on the field instead of being queued for a save that cannot succeed.
                  const value = toOptionalNumber(event.target.value);
                  const accepted = fitsSchema(amountSchema, value);

                  setAskingPriceOutOfRange(!accepted);
                  onChange({ customerAskingPrice: event.target.value }, accepted ? { customerAskingPrice: value } : null);
                }}
              />
            </FormField>

            <FormField
              id="estimate-cta-offer"
              label={t('estimates.ctaOffer')}
              hint={t('estimates.ctaOfferHint')}
            >
              <Input
                id="estimate-cta-offer"
                type="number"
                min={0}
                step={0.001}
                disabled={isClosed}
                value={offerTouched ? offer : detail.ctaOffer === null ? (ceiling === null ? '' : String(ceiling)) : String(detail.ctaOffer)}
                onChange={(event) => {
                  setOfferTouched(true);
                  setOffer(event.target.value);
                }}
              />
            </FormField>
          </div>

          {difference !== null ? (
            <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              {t(difference >= 0 ? 'estimates.offerAboveAsking' : 'estimates.offerBelowAsking', { amount: formatMoney(Math.abs(difference)) })}
            </p>
          ) : null}

          {aboveCeiling ? (
            <div className="space-y-3 rounded-md border border-[#a16207]/30 bg-[#a16207]/5 px-3 py-3 text-sm text-[#a16207]">
              <p>{t('estimates.aboveCeilingAlert', { amount: formatMoney(Math.abs((offerValue ?? 0) - (ceiling ?? 0))) })}</p>
              <div className="flex flex-wrap items-center gap-2">
                {approval ? (
                  <Badge tone={approval.isStale ? 'danger' : (APPROVAL_TONES[approval.status] ?? 'muted')}>
                    {approval.isStale ? t('approvals.stale') : t(`approvals.statuses.${approval.status}`)}
                    {approval.purchaseMax === null ? '' : ` · ${formatMoney(approval.purchaseMax)}`}
                  </Badge>
                ) : null}
                {user?.role === 'ADMIN' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    isLoading={isBusy}
                    disabled={isClosed || approvalBlocker !== null || isApprovalSettled}
                    onClick={() => void approveInline()}
                  >
                    {t('estimates.approveUpTo', { amount: formatMoney(offerValue ?? 0) })}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    isLoading={isBusy}
                    disabled={isClosed || approvalBlocker !== null || isApprovalSettled || isApprovalWaiting}
                    onClick={() => void requestInline()}
                  >
                    {t('estimates.requestApproval')}
                  </Button>
                )}
              </div>
              {isClosed || approvalBlocker === null ? null : <p className="text-xs">{t(approvalBlocker)}</p>}
            </div>
          ) : null}
        </section>

        <section className="space-y-4 rounded-lg border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-card-foreground">{t('estimates.outcomeTitle')}</h2>
          <p className="text-sm text-muted-foreground">{t('estimates.outcomeHelp')}</p>
          <p className="text-sm text-muted-foreground">{t('estimates.outcomeBody')}</p>

          {error === null ? null : (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              <p>{error}</p>
              {errorReasons.length === 0 ? null : (
                <ul className="mt-1 list-inside list-disc space-y-1">
                  {errorReasons.map((blocker) => (
                    <li key={blocker}>{t(`estimates.blockers.${blocker}`, { defaultValue: blocker })}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {detail.convertedVehicle ? (
            <div className="rounded-md border border-[#15803d]/30 bg-[#15803d]/5 px-3 py-2 text-sm text-[#15803d]">
              {t('estimates.purchasedNotice')}{' '}
              <Link
                to={`/vehicles/${detail.convertedVehicle.id}`}
                className="font-mono font-medium underline"
              >
                {detail.convertedVehicle.stockNo}
              </Link>
            </div>
          ) : null}

          {isClosed ? null : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  isLoading={shareOffer.isPending}
                  onClick={() =>
                    void run(async () =>
                      shareOffer.mutateAsync({
                        estimateId: detail.id,
                        expectedRevision: detail.revision,
                        ...(offerValue === null ? {} : { ctaOffer: offerValue }),
                      }),
                    )
                  }
                >
                  {t('estimates.shareOffer')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setNoDealOpen(true)}
                >
                  {t('estimates.noDeal')}
                </Button>
                <Button
                  disabled={!detail.canPurchase}
                  onClick={() => {
                    setPurchase({
                      price: detail.ctaOffer === null ? '' : String(detail.ctaOffer),
                      date: today,
                      status: 'IN_WAREHOUSE',
                      location: 'Amman used-vehicle warehouse',
                    });
                    setPurchaseOpen(true);
                  }}
                >
                  <Car className="size-4" />
                  {t('estimates.purchase')}
                </Button>
              </div>

              {detail.canPurchase ? null : (
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {detail.purchaseBlockers.map((blocker) => (
                    <li key={blocker}>{t(`estimates.blockers.${blocker}`, { defaultValue: blocker })}</li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      </div>

      <aside className="h-fit space-y-3 lg:sticky lg:top-6">
        <p className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">{t('estimates.ceilingNote')}</p>
        {detail.latestSnapshot ? (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('estimates.latestSnapshot')}</p>
            <p className="mt-1 text-sm text-foreground">
              #{detail.latestSnapshot.seq} · {formatMoney(detail.latestSnapshot.ceiling)}
            </p>
            {detail.latestSnapshot.isStale ? <Badge tone="warning">{t('estimates.snapshotStale')}</Badge> : null}
          </div>
        ) : null}
      </aside>

      <Dialog
        isOpen={noDealOpen}
        onClose={() => setNoDealOpen(false)}
        title={t('estimates.noDealTitle')}
        description={t('estimates.noDealBody')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setNoDealOpen(false)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              isLoading={closeNoDeal.isPending}
              disabled={noDealReason.trim() === ''}
              onClick={() =>
                void run(async () => {
                  await closeNoDeal.mutateAsync({ estimateId: detail.id, expectedRevision: detail.revision, reason: noDealReason.trim() });
                  setNoDealOpen(false);
                })
              }
            >
              {t('estimates.noDealConfirm')}
            </Button>
          </>
        }
      >
        <FormField
          id="no-deal-reason"
          label={t('estimates.noDealReason')}
        >
          <Textarea
            id="no-deal-reason"
            rows={3}
            value={noDealReason}
            placeholder={t('estimates.noDealPlaceholder')}
            onChange={(event) => setNoDealReason(event.target.value)}
          />
        </FormField>
      </Dialog>

      <Dialog
        isOpen={purchaseOpen}
        onClose={() => setPurchaseOpen(false)}
        title={t('estimates.purchaseTitle')}
        description={t('estimates.purchaseBody')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setPurchaseOpen(false)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              isLoading={confirmPurchase.isPending}
              disabled={!canConfirmPurchase}
              onClick={() =>
                void run(async () => {
                  await confirmPurchase.mutateAsync({
                    estimateId: detail.id,
                    expectedRevision: detail.revision,
                    purchasePrice: purchasePrice ?? 0,
                    purchaseDate: purchase.date,
                    initialStatus: purchase.status as 'IN_WAREHOUSE' | 'IN_SERVICE',
                    storageLocation: purchase.location.trim(),
                  });
                  setPurchaseOpen(false);
                })
              }
            >
              {t('estimates.purchaseConfirm')}
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            id="purchase-price"
            label={t('estimates.purchasePrice')}
          >
            <Input
              id="purchase-price"
              type="number"
              min={0.001}
              step={0.001}
              value={purchase.price}
              onChange={(event) => setPurchase((current) => ({ ...current, price: event.target.value }))}
            />
          </FormField>
          <FormField
            id="purchase-date"
            label={t('estimates.purchaseDate')}
          >
            <Input
              id="purchase-date"
              type="date"
              max={today}
              value={purchase.date}
              onChange={(event) => setPurchase((current) => ({ ...current, date: event.target.value }))}
            />
          </FormField>
          <FormField
            id="purchase-status"
            label={t('estimates.initialStatus')}
          >
            <Select
              id="purchase-status"
              value={purchase.status}
              onChange={(event) => setPurchase((current) => ({ ...current, status: event.target.value }))}
              options={[
                { value: 'IN_WAREHOUSE', label: t('enums.inventoryStatus.IN_WAREHOUSE') },
                { value: 'IN_SERVICE', label: t('enums.inventoryStatus.IN_SERVICE') },
              ]}
            />
          </FormField>
          <FormField
            id="purchase-location"
            label={t('estimates.storageLocation')}
          >
            <Input
              id="purchase-location"
              value={purchase.location}
              onChange={(event) => setPurchase((current) => ({ ...current, location: event.target.value }))}
            />
          </FormField>
        </div>

        {purchasePriceBlockers.length === 0 ? null : (
          <ul className="mt-4 list-inside list-disc space-y-1 text-sm text-destructive">
            {purchasePriceBlockers.map((blocker) => (
              <li key={blocker}>{t(`estimates.blockers.${blocker}`, { defaultValue: blocker })}</li>
            ))}
          </ul>
        )}
      </Dialog>
    </div>
  );
};

/** Money stays latin and LTR even in Arabic — a price is read as a number, not as prose (plan §2.7). */
const Money = ({ value, exact = false }: { value: number | null | undefined; exact?: boolean }) => (
  <span
    dir="ltr"
    className="tabular-nums"
  >
    {formatMoney(value, { exact })}
  </span>
);
